import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, PlusCircle, ListChecks, Wrench, Settings,
  BarChart3, Users, Download, LogOut, Menu, X,
} from "lucide-react";
import { useState } from "react";
import { cn } from "../lib/utils";

const LOGO = "https://static.prod-images.emergentagent.com/jobs/9aa213a6-9b93-496d-b454-969c7d67f960/images/80b5831e8eb35a7d67db619b6b98959a8312a899cbdbc39b43bf61ef5e0fcc19.png";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "frontdesk"] },
  { to: "/complaints/new", label: "New Complaint", icon: PlusCircle, roles: ["admin", "frontdesk"] },
  { to: "/complaints", label: "All Complaints", icon: ListChecks, roles: ["admin", "frontdesk"] },
  { to: "/engineer", label: "My Jobs", icon: Wrench, roles: ["engineer"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "frontdesk"] },
  { to: "/team", label: "Team & Users", icon: Users, roles: ["admin"] },
  { to: "/export", label: "Export Data", icon: Download, roles: ["admin"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-border flex items-center gap-3">
        <img src={LOGO} alt="RoSo" className="h-9 w-9 object-contain" />
        <div>
          <div className="font-heading font-black text-base leading-none">RoSo</div>
          <div className="label-caps text-muted-foreground mt-1" style={{ fontSize: "0.6rem" }}>
            Service Desk
          </div>
        </div>
      </div>
      <nav className="flex-1 py-4">
        {items.map((n) => {
          const active = location.pathname === n.to || (n.to !== "/" && location.pathname.startsWith(n.to));
          const Icon = n.icon;
          return (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setOpen(false)}
              className={cn(
                "flex items-center gap-3 px-5 py-3 text-sm font-medium border-l-2 transition-all duration-200",
                active
                  ? "border-klein bg-klein/5 text-klein"
                  : "border-transparent text-muted-foreground hover:text-ink hover:bg-secondary"
              )}
            >
              <Icon size={18} strokeWidth={1.75} />
              {n.label}
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-border p-4">
        <div className="mb-3">
          <div className="text-sm font-semibold truncate">{user?.name}</div>
          <div className="label-caps text-muted-foreground" style={{ fontSize: "0.6rem" }}>
            {user?.role}
          </div>
        </div>
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="flex items-center gap-2 text-sm text-signal font-medium hover:opacity-70 transition-opacity"
        >
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 border-r border-border bg-white flex-col fixed h-screen">
        {SidebarInner}
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-border flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <img src={LOGO} alt="RoSo" className="h-7 w-7 object-contain" />
          <span className="font-heading font-black">RoSo Service Desk</span>
        </div>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden fixed inset-0 z-20 pt-14">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-14 bottom-0 w-64 bg-white border-r border-border">
            {SidebarInner}
          </aside>
        </div>
      )}

      <main className="flex-1 md:ml-60 pt-14 md:pt-0 min-w-0">{children}</main>
    </div>
  );
}
